import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import Version from '@/components/version';
import { FAILED_IMAGE_BASE_64 } from '@/constants/general';

import { version } from '../../package.json';

interface ExtendedError extends Error {
  response?: unknown;
  status?: number;
  statusText?: string;
  config?: unknown;
  code?: string | number;
  cause?: unknown;
}

interface CreatePaymentFailedProps {
  error?: ExtendedError;
  onReset?: () => void;
}

const CreatePaymentFailed = ({ error }: CreatePaymentFailedProps) => {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (error) {
      try {
        // 安全地獲取瀏覽器資訊
        const getBrowserInfo = () => {
          try {
            return {
              userAgent: navigator?.userAgent ?? 'Unknown',
              language: navigator?.language ?? 'Unknown',
              platform: navigator?.platform ?? 'Unknown',
              screenResolution: `${window?.screen?.width ?? 0}x${window?.screen?.height ?? 0}`,
              viewportSize: `${window?.innerWidth ?? 0}x${window?.innerHeight ?? 0}`,
              cookieEnabled: navigator?.cookieEnabled ?? false,
              onLine: navigator?.onLine ?? true,
            };
          } catch {
            return { error: 'Failed to get browser info' };
          }
        };

        // 安全地獲取路由資訊
        const getLocationInfo = () => {
          try {
            return {
              currentUrl: window?.location?.href ?? 'Unknown',
              pathname: window?.location?.pathname ?? 'Unknown',
              searchParams: window?.location?.search ?? '',
              hash: window?.location?.hash ?? '',
              origin: window?.location?.origin ?? 'Unknown',
              protocol: window?.location?.protocol ?? 'Unknown',
              host: window?.location?.host ?? 'Unknown',
            };
          } catch {
            return { error: 'Failed to get location info' };
          }
        };

        Sentry.captureException(error, {
          tags: {
            component: 'CreatePaymentFailed',
            errorType: error?.name ?? 'Unknown',
            route: window?.location?.pathname ?? 'Unknown',
            environment: import.meta.env.VITE_BRAND ?? 'Unknown',
            mode: import.meta.env.MODE ?? 'Unknown',
            language: i18n?.language ?? 'Unknown',
          },
          extra: {
            // 完整的 error object
            fullErrorObject: JSON.parse(JSON.stringify(error, Object.getOwnPropertyNames(error))),

            // 錯誤基本資訊
            errorMessage: error?.message ?? 'No error message',
            errorStack: error?.stack ?? 'No stack trace',
            errorName: error?.name ?? 'Unknown error',
            errorCause: error?.cause ?? undefined,

            // 時間資訊
            timestamp: new Date().toISOString(),
            timestampUnix: Date.now(),

            // 應用程式資訊
            appVersion: version ?? 'Unknown',
            appEnvironment: `${import.meta.env.VITE_BRAND ?? 'unknown'}-${import.meta.env.VITE_ENVIRONMENT ?? 'unknown'}`,

            // 瀏覽器資訊
            browserInfo: getBrowserInfo(),

            // 路由資訊
            locationInfo: getLocationInfo(),

            // 其他可能的錯誤屬性（如 API 錯誤）
            errorResponse: (error as ExtendedError)?.response ?? undefined,
            errorStatus: (error as ExtendedError)?.status ?? undefined,
            errorStatusText: (error as ExtendedError)?.statusText ?? undefined,
            errorConfig: (error as ExtendedError)?.config ?? undefined,
            errorCode: (error as ExtendedError)?.code ?? undefined,
          },
          contexts: {
            app: {
              app_name: 'paybox-web-v2',
              app_version: version ?? 'Unknown',
              environment: import.meta.env.VITE_ENVIRONMENT ?? 'Unknown',
              brand: import.meta.env.VITE_BRAND ?? 'Unknown',
            },
            browser: getBrowserInfo(),
            device: {
              screen_resolution: `${window?.screen?.width ?? 0}x${window?.screen?.height ?? 0}`,
              viewport_size: `${window?.innerWidth ?? 0}x${window?.innerHeight ?? 0}`,
            },
          },
          level: 'error',
        });
      } catch (sentryError) {
        // 如果 Sentry 本身出錯，至少記錄到 console
        console.error('Failed to send error to Sentry:', sentryError);
        console.error('Original error:', error);
      }
    }
  }, [error, i18n?.language]);

  return (
    <main className="flex items-center justify-center h-full w-full px-4">
      <div className="flex flex-col items-center justify-center bg-main-white pt-16 pb-14 px-8 rounded-xl shadow-custom w-[340px]">
        <img src={FAILED_IMAGE_BASE_64} alt="Failed" className="mb-8 w-24" />
        <div className="text-neutral-975 text-sm">{t('order_created_failed')}</div>
      </div>
      <Version />
    </main>
  );
};

export default CreatePaymentFailed;
